import axios from 'axios';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../component/Footer';

/* ── simple card exactly matching the screenshot ── */
function CourseCard({ course }) {
    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-5 flex flex-col h-full">
            {/* title */}
            <h3 className="text-base font-bold text-gray-900 leading-snug mb-1 line-clamp-2">{course.title}</h3>
            {/* description */}
            {course.description && (
                <p className="text-sm text-gray-500 line-clamp-3 mb-3 flex-1">{course.description}</p>
            )}
            {/* divider */}
            <div className="border-t border-gray-100 my-2" />
            {/* meta row */}
            <div className="flex items-center justify-between mt-1">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">{course.total_lesson ?? 0} Bài học</span>
                    {course.level && (
                        <span className="text-xs font-semibold bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">{course.level}</span>
                    )}
                </div>
                <Link
                    to={`/lesson/${course.slug}`}
                    className="bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-4 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                >
                    Học ngay
                </Link>
            </div>
        </div>
    );
}

/* ── section with heading + 3-col grid ── */
function CourseSection({ title, courses }) {
    if (!courses || courses.length === 0) return null;
    return (
        <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{title}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {courses.map((c) => (
                    <CourseCard key={c.id} course={c} />
                ))}
            </div>
        </section>
    );
}

export default function Home() {
    const [dataCourse, setDataCourse] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCourses = async () => {
            try {
                setLoading(true);
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/course/getAll`, { params: { page: 1, pageSize: 100 } });
                setDataCourse(res?.data?.data || []);
            } catch (err) {
                console.error('Failed to load courses', err);
            } finally {
                setLoading(false);
            }
        };
        fetchCourses();
    }, []);

    /* Group courses by topic. First topic = "Chương trình học chính",
       remaining topics = "Chương trình bổ sung" (grouped together).
       If there's only 1 topic, show all under "Chương trình học chính". */
    const grouped = {};
    dataCourse.forEach((c) => {
        const key = c.topic_title || 'Khác';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(c);
    });
    const topicKeys = Object.keys(grouped);
    const mainKey   = topicKeys[0] || null;
    const mainCourses = mainKey ? grouped[mainKey] : [];
    const extraCourses = topicKeys.slice(1).flatMap((k) => grouped[k]);

    return (
        <>
            <div className="min-h-screen bg-[#eef9ff] py-8">
                <div className="max-w-screen-xl mx-auto px-6">
                    {/* Hero header */}
                    <header className="text-center mb-8">
                        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-800">
                            Chào mừng đến với <span className="text-blue-500">Effortless English</span>
                        </h1>
                        <p className="mt-2 text-gray-500 text-sm md:text-base">
                            Học Tiếng Anh theo Phương Pháp Effortless English – Tự nhiên, dễ dàng và hiệu quả
                        </p>
                    </header>

                    {loading ? (
                        <div className="flex justify-center py-16">
                            <div className="animate-spin rounded-full border-4 border-t-transparent border-blue-500 h-10 w-10" />
                        </div>
                    ) : (
                        <>
                            <CourseSection title="Chương trình học chính" courses={mainCourses} />
                            {extraCourses.length > 0 && (
                                <CourseSection title="Chương trình bổ sung" courses={extraCourses} />
                            )}
                        </>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
}